import React, { useState } from "react";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import ErrorIcon from "@mui/icons-material/Error";

const basePath =
  process.env.NODE_ENV === "production" ? "/edge" : "http://localhost:3010";

const oneMegabyte = 1000000;

export default function UploadButton({
  name,
  onUploadComplete,
  onUploadStatusChange,
  acceptTypes = "image/*",
  fileSizeLimitInMB = 10,
  disableMargin = false,
  hideStatus = false,
  buttonText = "Upload File",
  disabled = false,
  iconPosition = "start",
}) {
  const [uploadStatus, setUploadStatus] = useState(null);
  const uploadDisabled = disabled || uploadStatus?.state === "load";

  const handleFileChange = async (event) => {
    const input = event.currentTarget;
    const file = input.files[0];

    if (file) {
      if (file.size > fileSizeLimitInMB * oneMegabyte) {
        const nextStatus = {
          state: "error",
          text: `File size limit is ${fileSizeLimitInMB}MB`,
        };
        setUploadStatus(nextStatus);
        if (onUploadStatusChange) {
          onUploadStatusChange({ name, ...nextStatus });
        }
        input.value = "";
        return;
      }

      const formData = new FormData();
      formData.append("file", file);

      try {
        const nextStatus = { state: "load", text: "Uploading.." };
        setUploadStatus(nextStatus);
        if (onUploadStatusChange) {
          onUploadStatusChange({ name, ...nextStatus });
        }
        const response = await fetch(`${basePath}/api/v1/upload`, {
          method: "POST",
          credentials: "include",
          body: formData,
        });

        if (response.ok) {
          const jsonResponse = await response.json();

          const doneStatus = { state: "done", text: file.name };
          setUploadStatus(doneStatus);
          if (onUploadStatusChange) {
            onUploadStatusChange({ name, ...doneStatus });
          }
          onUploadComplete({
            name,
            file,
            tmpFilename: jsonResponse.tmpFilename,
          });
        } else {
          const errStatus = { state: "error", text: "Upload failed" };
          setUploadStatus(errStatus);
          if (onUploadStatusChange) {
            onUploadStatusChange({ name, ...errStatus });
          }
        }
      } catch (error) {
        const errStatus = {
          state: "error",
          text: "Error during upload process",
        };
        setUploadStatus(errStatus);
        if (onUploadStatusChange) {
          onUploadStatusChange({ name, ...errStatus });
        }
      } finally {
        input.value = "";
      }
    }
  };

  return (
    <>
      <input
        accept={acceptTypes}
        style={{ display: "none" }}
        id={name}
        multiple
        type="file"
        disabled={uploadDisabled}
        onChange={handleFileChange}
      />
      <label htmlFor={name}>
        <Button
          variant="contained"
          color="primary"
          component="span"
          disabled={uploadDisabled}
          sx={disableMargin ? { m: 0 } : { m: 1 }}
          startIcon={iconPosition === "start" ? <CloudUploadIcon /> : undefined}
          endIcon={iconPosition === "end" ? <CloudUploadIcon /> : undefined}
        >
          {buttonText}
        </Button>
        {!hideStatus && uploadStatus !== null && (
          <Paper
            sx={{
              mt: "4px",
              ml: "10px",
              mb: "6px",
              border: "solid 1px #999999",
              width: "fit-content",
              pr: "8px",
              display: "flex",
              borderRadius: "5px",
              p: "4px",
            }}
          >
            {uploadStatus.state === "load" && (
              <CircularProgress
                sx={{ width: 24, height: 24 }}
                color="primary"
              />
            )}
            {uploadStatus.state === "done" && (
              <CloudDoneIcon
                sx={{ width: 24, height: 24 }}
                color="primary"
              />
            )}
            {uploadStatus.state === "error" && (
              <ErrorIcon
                sx={{ width: 24, height: 24 }}
                color="primary"
              />
            )}
            <Typography
              sx={{
                pl: "7px",
                fontSize: "13px",
                position: "relative",
                top: "2px",
                color: "#999999",
              }}
            >
              {uploadStatus.text}
            </Typography>
          </Paper>
        )}
      </label>
    </>
  );
}
